import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Activity, TrendingUp, Users, Clock, Target, Calendar, CheckCircle2, XCircle, AlertTriangle,
  FileText, ShoppingCart, CreditCard, UserPlus, MessageSquare, Palmtree, Phone, Mail, Edit, Trash2,
  BarChart3, PieChart, ArrowUpRight, ArrowDownRight, ListTodo, Repeat
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow, format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

// Activity type icons and colors
const ACTIVITY_CONFIG = {
  status_changed: { icon: Edit, color: 'bg-blue-500', label: 'Durum Değişikliği' },
  request_created: { icon: FileText, color: 'bg-blue-500', label: 'Yeni Talep' },
  request_updated: { icon: Edit, color: 'bg-blue-400', label: 'Talep Güncellendi' },
  quote_created: { icon: FileText, color: 'bg-indigo-500', label: 'Teklif Oluşturuldu' },
  quote_updated: { icon: Edit, color: 'bg-indigo-400', label: 'Teklif Güncellendi' },
  assigned: { icon: UserPlus, color: 'bg-purple-500', label: 'Atama Yapıldı' },
  note_added: { icon: MessageSquare, color: 'bg-slate-500', label: 'Not Eklendi' },
  sale_created: { icon: ShoppingCart, color: 'bg-emerald-500', label: 'Yeni Satış' },
  customer_created: { icon: UserPlus, color: 'bg-purple-500', label: 'Yeni Müşteri' },
  customer_updated: { icon: Users, color: 'bg-purple-400', label: 'Müşteri Güncellendi' },
  payment_created: { icon: CreditCard, color: 'bg-amber-500', label: 'Ödeme Talebi' },
  payment_approved: { icon: CheckCircle2, color: 'bg-green-500', label: 'Ödeme Onaylandı' },
  payment_rejected: { icon: XCircle, color: 'bg-red-500', label: 'Ödeme Reddedildi' },
  leave_created: { icon: Palmtree, color: 'bg-teal-500', label: 'İzin Talebi' },
  leave_approved: { icon: CheckCircle2, color: 'bg-green-500', label: 'İzin Onaylandı' },
  leave_rejected: { icon: XCircle, color: 'bg-red-500', label: 'İzin Reddedildi' },
  task_created: { icon: Target, color: 'bg-indigo-500', label: 'Yeni Görev' },
  task_completed: { icon: CheckCircle2, color: 'bg-green-500', label: 'Görev Tamamlandı' },
  meeting_created: { icon: Calendar, color: 'bg-cyan-500', label: 'Yeni Toplantı' },
  message_sent: { icon: MessageSquare, color: 'bg-pink-500', label: 'Mesaj' },
  call_made: { icon: Phone, color: 'bg-orange-500', label: 'Arama' },
  email_sent: { icon: Mail, color: 'bg-sky-500', label: 'E-posta' },
  default: { icon: Activity, color: 'bg-slate-500', label: 'Aktivite' }
};

const translateStatus = (text) => {
  if (!text) return text;
  const translations = {
    'won': 'Kazanıldı', 'lost': 'Kaybedildi', 'new': 'Yeni',
    'contacted': 'İletişime Geçildi', 'quoted': 'Teklif Verildi',
    'pending': 'Beklemede', 'approved': 'Onaylandı', 'rejected': 'Reddedildi',
    'completed': 'Tamamlandı', 'cancelled': 'İptal Edildi', 'paid': 'Ödendi',
    'status_changed': 'Durum Değişti', 'Durum değiştirildi:': 'Durum:',
  };
  let result = text;
  Object.entries(translations).forEach(([en, tr]) => {
    result = result.replace(new RegExp(en, 'gi'), tr);
  });
  return result;
};

const getActivityConfig = (type, description = '') => {
  if (ACTIVITY_CONFIG[type]) return ACTIVITY_CONFIG[type];
  const desc = description.toLowerCase();
  if (desc.includes('won') || desc.includes('kazanıldı')) return { icon: CheckCircle2, color: 'bg-green-500', label: 'Kazanıldı' };
  if (desc.includes('lost') || desc.includes('kaybedildi')) return { icon: XCircle, color: 'bg-red-500', label: 'Kaybedildi' };
  if (desc.includes('new') || desc.includes('yeni')) return { icon: FileText, color: 'bg-blue-500', label: 'Yeni Talep' };
  if (desc.includes('teklif') || desc.includes('quote')) return { icon: FileText, color: 'bg-indigo-500', label: 'Teklif' };
  if (desc.includes('satış') || desc.includes('sale')) return { icon: ShoppingCart, color: 'bg-emerald-500', label: 'Satış' };
  return ACTIVITY_CONFIG.default;
};

// Chart colors
const COLORS = ['#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

// Custom tooltip content for area chart
const CustomAreaTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const talepler = payload.find(p => p.dataKey === 'talepler');
    const kazanilan = payload.find(p => p.dataKey === 'kazanilan');
    const satislar = payload.find(p => p.dataKey === 'satislar');
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg">
        <p className="font-semibold text-gray-700 mb-2">{label}</p>
        {talepler && (
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#06b6d4' }}></div>
            <span className="text-sm text-gray-600">Talepler:</span>
            <span className="text-sm font-semibold text-gray-800">{talepler.value}</span>
          </div>
        )}
        {kazanilan && (
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#10b981' }}></div>
            <span className="text-sm text-gray-600">Kazanılan:</span>
            <span className="text-sm font-semibold text-gray-800">{kazanilan.value}</span>
          </div>
        )}
        {satislar && (
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#f59e0b' }}></div>
            <span className="text-sm text-gray-600">Satışlar:</span>
            <span className="text-sm font-semibold text-gray-800">{satislar.value}</span>
          </div>
        )}
      </div>
    );
  }
  return null;
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [chartData, setChartData] = useState({ weekly: [], statusPie: [], monthlyTrend: [] });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, activityRes, monthlyTrendRes, weeklyTrendRes, tasksRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`).catch(() => ({ data: null })),
        axios.get(`${API}/dashboard/recent-activity`).catch(() => ({ data: [] })),
        axios.get(`${API}/dashboard/monthly-trend`).catch(() => ({ data: [] })),
        axios.get(`${API}/dashboard/weekly-trend`).catch(() => ({ data: [] })),
        axios.get(`${API}/tasks`).catch(() => ({ data: [] })),
      ]);

      if (statsRes.data) {
        setStats(statsRes.data);
      }
      setRecentActivity(activityRes.data || []);
      
      // Bekleyen görevleri filtrele (pending veya in_progress, deadline yaklaşanlar önce)
      const tasks = tasksRes.data || [];
      const pending = tasks
        .filter(t => t.status === 'pending' || t.status === 'in_progress')
        .sort((a, b) => {
          if (a.deadline && !b.deadline) return -1;
          if (!a.deadline && b.deadline) return 1;
          if (a.deadline && b.deadline) {
            return new Date(a.deadline) - new Date(b.deadline);
          }
          const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
          return (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
        })
        .slice(0, 6);
      setPendingTasks(pending);

      generateChartData(statsRes.data, monthlyTrendRes.data || [], weeklyTrendRes.data || []);
    } catch (error) {
      toast.error('Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const generateChartData = (stats, monthlyTrendData = [], weeklyTrendData = []) => {
    // Status pie chart data - using correct field names from API
    const statusPie = [
      { name: 'Bekleyen', value: stats?.new_requests || 0, color: '#3b82f6' },
      { name: 'İnceleniyor', value: stats?.in_review || 0, color: '#8b5cf6' },
      { name: 'Teklif Verildi', value: stats?.quoted || 0, color: '#f59e0b' },
      { name: 'Kazanıldı', value: stats?.won || 0, color: '#10b981' },
      { name: 'Kaybedildi', value: stats?.lost || 0, color: '#ef4444' },
    ].filter(item => item.value > 0);

    // Use real weekly data from API
    const weekly = weeklyTrendData.length > 0 ? weeklyTrendData : [];

    // Use real monthly trend data from API - map backend fields to chart field names
    const monthlyTrend = monthlyTrendData.length > 0 
      ? monthlyTrendData.map(item => ({
          name: item.name,
          talepler: item.total || item.talepler || 0,
          kazanilan: item.won || item.kazanilan || 0,
          satislar: item.sales || item.satislar || 0
        }))
      : [];

    setChartData({ weekly, statusPie, monthlyTrend });
  };

  const handleCardClick = (status) => {
    navigate(`/requests?status=${status}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Toplam Talepler',
      value: stats?.total_requests || 0,
      icon: Activity,
      color: 'from-cyan-500 to-blue-500',
      bgColor: 'bg-cyan-50',
      iconColor: 'text-cyan-600',
      change: stats?.requests_change !== undefined ? `${stats.requests_change >= 0 ? '+' : ''}${stats.requests_change}%` : null,
      changeType: stats?.requests_change >= 0 ? 'up' : 'down',
      onClick: () => navigate('/requests'),
    },
    {
      title: 'Yeni Talepler',
      value: stats?.new_requests || 0,
      icon: Target,
      color: 'from-blue-500 to-indigo-500',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      change: stats?.new_requests_change !== undefined ? `${stats.new_requests_change >= 0 ? '+' : ''}${stats.new_requests_change}%` : null,
      changeType: stats?.new_requests_change >= 0 ? 'up' : 'down',
      onClick: () => handleCardClick('new'),
    },
    {
      title: 'Kazanma Oranı',
      value: `${stats?.win_rate || 0}%`,
      icon: TrendingUp,
      color: 'from-emerald-500 to-green-500',
      bgColor: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      change: stats?.win_rate_change !== undefined ? `${stats.win_rate_change >= 0 ? '+' : ''}${stats.win_rate_change}%` : null,
      changeType: stats?.win_rate_change >= 0 ? 'up' : 'down',
    },
    {
      title: 'Ort. Dönüş Süresi',
      value: stats?.avg_response_time_hours ? `${stats.avg_response_time_hours} saat` : '-',
      icon: Clock,
      color: 'from-cyan-500 to-blue-500',
      bgColor: 'bg-cyan-50',
      iconColor: 'text-cyan-600',
      change: null,
      changeType: 'neutral',
    },
    {
      title: 'Toplam Müşteri',
      value: stats?.total_customers || 0,
      icon: Users,
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      change: stats?.customers_change !== undefined ? `${stats.customers_change >= 0 ? '+' : ''}${stats.customers_change}%` : null,
      changeType: stats?.customers_change >= 0 ? 'up' : 'down',
      onClick: () => navigate('/customers'),
    },
  ];

  const statusCards = [
    { label: 'Bekleyen', value: stats?.new_requests || 0, icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50', status: 'new' },
    { label: 'Teklif Verildi', value: stats?.quoted || 0, icon: FileText, color: 'text-amber-600', bg: 'bg-amber-50', status: 'quoted' },
    { label: 'Kazanıldı', value: stats?.won || 0, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50', status: 'won' },
    { label: 'Kaybedildi', value: stats?.lost || 0, icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', status: 'lost' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Genel Bakış</h1>
          <p className="text-gray-500 mt-1">Hoş geldiniz! İşte ekibinizin genel durumu.</p>
        </div>
        <Button onClick={() => navigate('/reports')} variant="outline" className="gap-2">
          <BarChart3 className="w-4 h-4" />
          Detaylı Raporlar
        </Button>
      </div>

      {/* Alert for pending requests */}
      {(stats?.new_requests > 5 || stats?.response_warning_count > 0) && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <div>
            <p className="font-medium text-amber-800">Dikkat!</p>
            <p className="text-sm text-amber-700">
              {stats?.response_warning_count > 0 
                ? `${stats.response_warning_count} talep 24 saati aştı.` 
                : `${stats?.new_requests} yeni talep bekliyor.`}
            </p>
          </div>
          <Button 
            size="sm" 
            variant="outline" 
            className="ml-auto border-amber-300 text-amber-700 hover:bg-amber-100"
            onClick={() => navigate('/requests?status=new')}
          >
            İncele
          </Button>
        </div>
      )}

      {/* Main Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {statCards.map((stat, index) => (
          <Card 
            key={index} 
            className={`relative overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300 hover:-translate-y-1 ${stat.onClick ? '' : ''}`}
            onClick={stat.onClick}
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5`} />
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                  <stat.icon className={`w-5 h-5 ${stat.iconColor}`} />
                </div>
                {stat.change && (
                  <div 
                    className={`flex items-center gap-0.5 text-xs font-medium ${stat.changeType === 'up' ? 'text-emerald-600' : 'text-red-600'}`}
                    title="Geçen aya göre değişim"
                  >
                    {stat.changeType === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                    {stat.change}
                  </div>
                )}
              </div>
              <div className="mt-3">
                <p className="text-xs text-gray-500">{stat.title}</p>
                <p className="text-xl font-bold text-gray-900 mt-0.5">{stat.value}</p>
                {stat.change && (
                  <p className="text-[10px] text-gray-400 mt-0.5">Geçen aya göre</p>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Section - Pending Tasks + Monthly Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Tasks */}
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <ListTodo className="w-5 h-5 text-cyan-600" />
              Bekleyen Görevler
            </CardTitle>
            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => navigate('/tasks')}>
              Tümü
            </Button>
          </CardHeader>
          <CardContent>
            {pendingTasks.length === 0 ? (
              <div className="h-64 flex flex-col items-center justify-center text-gray-500">
                <CheckCircle2 className="w-12 h-12 mb-3 text-emerald-400" />
                <p>Bekleyen görev yok</p>
              </div>
            ) : (
              <div className="space-y-2 h-64 overflow-y-auto pr-1">
                {pendingTasks.map((task) => {
                  const isOverdue = task.deadline && new Date(task.deadline) < new Date();
                  const priorityColors = {
                    urgent: 'bg-red-500',
                    high: 'bg-orange-500',
                    medium: 'bg-blue-500',
                    low: 'bg-gray-400',
                  };
                  
                  return (
                    <div
                      key={task.id}
                      onClick={() => navigate(`/tasks/${task.id}`)}
                      className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer hover:bg-gray-50 transition-all border ${
                        isOverdue ? 'bg-red-50 border-red-200' : 'border-gray-100'
                      }`}
                    >
                      <div className={`w-2.5 h-2.5 rounded-full ${priorityColors[task.priority]} flex-shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900 truncate">{task.title}</p>
                          {task.recurrence && task.recurrence !== 'none' && (
                            <Repeat className="w-3.5 h-3.5 text-cyan-500 flex-shrink-0" />
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                          <span className="truncate">{task.assigned_to_name}</span>
                          {task.deadline && (
                            <>
                              <span>•</span>
                              <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
                                {format(new Date(task.deadline), 'dd MMM', { locale: tr })}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Monthly Trend Area Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              Aylık Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData.monthlyTrend}>
                  <defs>
                    <linearGradient id="colorTalepler" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorKazanilan" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorSatislar" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" stroke="#9ca3af" fontSize={12} />
                  <YAxis stroke="#9ca3af" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px' }}
                    content={CustomAreaTooltip}
                  />
                  <Area type="monotone" dataKey="talepler" stroke="#06b6d4" fill="url(#colorTalepler)" name="Talepler" />
                  <Area type="monotone" dataKey="kazanilan" stroke="#10b981" fill="url(#colorKazanilan)" name="Kazanılan" />
                  <Area type="monotone" dataKey="satislar" stroke="#f59e0b" fill="url(#colorSatislar)" name="Satışlar" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Cards + Activity + Pie Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Status Summary */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Durum Özeti</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {statusCards.map((item, index) => (
              <div 
                key={index}
                onClick={() => handleCardClick(item.status)}
                className={`flex items-center justify-between p-3 rounded-xl ${item.bg} cursor-pointer hover:scale-[1.02] transition-transform`}
              >
                <div className="flex items-center gap-3">
                  <item.icon className={`w-5 h-5 ${item.color}`} />
                  <span className="font-medium text-gray-700">{item.label}</span>
                </div>
                <span className={`text-xl font-bold ${item.color}`}>{item.value}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Son Aktiviteler</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[280px] overflow-y-auto">
              {recentActivity.length === 0 ? (
                <p className="text-gray-500 text-center py-4 text-sm">Henüz aktivite yok</p>
              ) : (
                recentActivity.slice(0, 6).map((activity, index) => {
                  const config = getActivityConfig(activity.type, activity.description);
                  const Icon = config.icon;
                  let detailText = translateStatus(activity.description);
                  
                  return (
                    <div 
                      key={activity.id || index} 
                      className={`flex items-center gap-2 p-2 rounded-lg transition-all ${activity.link ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                      onClick={() => activity.link && navigate(activity.link)}
                    >
                      <div className={`p-1.5 rounded-lg ${config.color} text-white flex-shrink-0`}>
                        <Icon className="w-3 h-3" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-900 truncate">{detailText}</p>
                        <p className="text-xs text-gray-400">
                          {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true, locale: tr })}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>

        {/* Status Distribution Pie Chart */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="w-5 h-5 text-cyan-600" />
              Talep Durumu Dağılımı
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="h-[180px] w-[180px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={chartData.statusPie}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={75}
                      paddingAngle={3}
                      dataKey="value"
                      label={({ value }) => value > 0 ? value : ''}
                      labelLine={false}
                    >
                      {chartData.statusPie.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                      formatter={(value, name) => [value, name]}
                    />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
              {/* Legend */}
              <div className="flex-1 space-y-2">
                {chartData.statusPie.map((entry, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></div>
                      <span className="text-gray-600">{entry.name}</span>
                    </div>
                    <span className="font-semibold text-gray-800">{entry.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
